import "moment/locale/de";

import { Box, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import moment from "moment";
import React from "react";

interface TimelineCardsProps {
  cards: any[];
  title?: string;
  primaryColor: string;
  onOpen: (card: any) => void;
}

const normalizeGermanQuotes = (text = "") => text.replace(/"([^"]+)"/g, "„$1“");

export default function TimelineCards({
  cards,
  title,
  primaryColor,
  onOpen,
}: TimelineCardsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sortedCards = [...cards].sort((a, b) => {
    const first = a.dateLabel ? new Date(a.dateLabel).getTime() : Infinity;
    const second = b.dateLabel ? new Date(b.dateLabel).getTime() : Infinity;
    return first - second;
  });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [transitionDirection, setTransitionDirection] = React.useState(1);
  const [animationToken, setAnimationToken] = React.useState(0);
  const timelineScrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => setSelectedIndex(0), [cards.length]);

  const useScrollableTimeline = isMobile || sortedCards.length > 8;

  React.useEffect(() => {
    if (!useScrollableTimeline) return;

    const selectedCard = timelineScrollRef.current?.querySelector(
      `[data-timeline-index="${selectedIndex}"]`
    );
    selectedCard?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [selectedIndex, useScrollableTimeline]);

  if (sortedCards.length === 0) return null;

  const selected = sortedCards[Math.min(selectedIndex, sortedCards.length - 1)];
  const timelinePixelWidth = Math.max(900, sortedCards.length * 210);
  const timelineWidth: number | string = useScrollableTimeline
    ? timelinePixelWidth
    : "100%";
  const timelineInset = useScrollableTimeline
    ? Math.min(24, Math.max(10, (92 / timelinePixelWidth) * 100))
    : 8;
  const alternateTimelineLabels = sortedCards.length > 3;

  const getPosition = (_card: any, index: number) => {
    if (sortedCards.length === 1) return 50;

    // Keep chronological order but use equal visual spacing. Strictly
    // proportional dates make projects from the same week/month overlap.
    return (
      timelineInset +
      (index / (sortedCards.length - 1)) * (100 - timelineInset * 2)
    );
  };
  const changeSelection = (direction: number) => {
    setTransitionDirection(direction);
    setAnimationToken((current) => current + 1);
    setSelectedIndex((current) => {
      const next = current + direction;
      return Math.max(0, Math.min(sortedCards.length - 1, next));
    });
  };

  const selectCard = (index: number) => {
    if (index === selectedIndex) return;
    setTransitionDirection(index > selectedIndex ? 1 : -1);
    setAnimationToken((current) => current + 1);
    setSelectedIndex(index);
  };

  const selectedKey = `${selected.id || selectedIndex}-${animationToken}`;
  const imageAnimation =
    transitionDirection > 0
      ? "cmsTimelineImageNext"
      : "cmsTimelineImagePrevious";
  const contentAnimation =
    transitionDirection > 0
      ? "cmsTimelineContentNext"
      : "cmsTimelineContentPrevious";

  return (
    <Box style={{ margin: title ? "32px 0" : "0 0 32px" }}>
      <style>{`
        @keyframes cmsTimelineImageNext {
          from { opacity: 0; transform: translateX(16px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes cmsTimelineImagePrevious {
          from { opacity: 0; transform: translateX(-16px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes cmsTimelineContentNext {
          from { opacity: 0; transform: translateX(22px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes cmsTimelineContentPrevious {
          from { opacity: 0; transform: translateX(-22px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cms-timeline-transition { animation: none !important; }
        }
      `}</style>
      {title && (
        <Box style={{ marginBottom: 28 }}>
          <Box>
            <Typography
              variant="h4"
              style={{ color: primaryColor, fontWeight: 800, marginBottom: 8 }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(0, 1.5fr) minmax(320px, 1fr)",
          height: isMobile ? "auto" : 460,
          minHeight: isMobile ? 560 : 0,
          backgroundColor: "#fff",
          border: "1px solid #e7e7e7",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          key={`image-${selectedKey}`}
          className="cms-timeline-transition"
          style={{
            position: "relative",
            height: isMobile ? 280 : "100%",
            minHeight: isMobile ? 280 : 0,
            backgroundColor: "#f2f3f4",
            overflow: "hidden",
            animation: `${imageAnimation} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          }}
        >
          {selected.imageUrl ? (
            <>
              <Box
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <img
                  src={selected.imageUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "cover",
                    filter: "blur(20px)",
                    opacity: 0.32,
                    transform: "scale(1.08)",
                  }}
                />
              </Box>
              <img
                src={selected.imageUrl}
                alt={normalizeGermanQuotes(selected.displayTitle)}
                loading="lazy"
                decoding="async"
                style={{
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ height: "100%", color: "#999", fontWeight: 700 }}
            >
              Kein Projektbild
            </Box>
          )}
        </Box>
        <Box
          key={`content-${selectedKey}`}
          className="cms-timeline-transition"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          style={{
            padding: isMobile ? 28 : 48,
            overflowY: "auto",
            animation: `${contentAnimation} 380ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          }}
        >
          {selected.dateLabel && (
            <Typography
              variant="body2"
              style={{ color: "#999", fontWeight: 700, marginBottom: 8 }}
            >
              {moment(selected.dateLabel).locale("de").format("DD. MMMM YYYY")}
            </Typography>
          )}
          {selected.displayTitle && (
            <Typography
              variant="h4"
              style={{ fontWeight: 800, lineHeight: 1.1, marginBottom: 16 }}
            >
              {normalizeGermanQuotes(selected.displayTitle)}
            </Typography>
          )}
          {selected.displayDescription && (
            <Typography
              variant="body1"
              style={{ color: "#555", lineHeight: 1.7, marginBottom: 24 }}
            >
              {selected.displayDescription}
            </Typography>
          )}
          {selected.link !== "#" && (
            <button
              type="button"
              onClick={() => onOpen(selected)}
              style={{
                alignSelf: "flex-start",
                border: 0,
                borderRadius: 9,
                padding: "11px 18px",
                backgroundColor: primaryColor,
                color: "#fff",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Mehr erfahren →
            </button>
          )}
        </Box>
        {sortedCards.length > 1 &&
          [-1, 1]
            .filter((direction) =>
              direction < 0
                ? selectedIndex > 0
                : selectedIndex < sortedCards.length - 1
            )
            .map((direction) => (
              <button
                type="button"
                key={direction}
                aria-label={
                  direction < 0 ? "Vorheriges Projekt" : "Nächstes Projekt"
                }
                onClick={() => changeSelection(direction)}
                style={{
                  position: "absolute",
                  left: direction < 0 ? 14 : "auto",
                  right: direction > 0 ? 14 : "auto",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  border: "1px solid #ddd",
                  background: "rgba(255,255,255,0.92)",
                  color: primaryColor,
                  fontSize: 28,
                  cursor: "pointer",
                  zIndex: 2,
                  boxShadow: "0 5px 18px rgba(0,0,0,0.12)",
                }}
              >
                {direction < 0 ? "‹" : "›"}
              </button>
            ))}
      </Box>

      {useScrollableTimeline && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ marginTop: 22, marginBottom: -12, gap: 12 }}
        >
          <Typography
            variant="body2"
            style={{ color: "#666", fontWeight: 700 }}
          >
            {selectedIndex + 1} / {sortedCards.length} Termine
          </Typography>
          <Box display="flex" style={{ gap: 8 }}>
            <button
              type="button"
              aria-label="Frühere Termine anzeigen"
              onClick={() => changeSelection(-1)}
              disabled={selectedIndex === 0}
              style={{
                border: "1px solid #ddd",
                borderRadius: "50%",
                width: 36,
                height: 36,
                padding: 0,
                background: "#fff",
                color: primaryColor,
                fontSize: 22,
                lineHeight: 1,
                cursor: selectedIndex === 0 ? "not-allowed" : "pointer",
                opacity: selectedIndex === 0 ? 0.42 : 1,
              }}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Spätere Termine anzeigen"
              onClick={() => changeSelection(1)}
              disabled={selectedIndex === sortedCards.length - 1}
              style={{
                border: "1px solid #ddd",
                borderRadius: "50%",
                width: 36,
                height: 36,
                padding: 0,
                background: "#fff",
                color: primaryColor,
                fontSize: 22,
                lineHeight: 1,
                cursor:
                  selectedIndex === sortedCards.length - 1
                    ? "not-allowed"
                    : "pointer",
                opacity: selectedIndex === sortedCards.length - 1 ? 0.42 : 1,
              }}
            >
              ›
            </button>
          </Box>
        </Box>
      )}

      <div
        ref={timelineScrollRef}
        style={{ overflowX: "auto", padding: "26px 8px 12px" }}
      >
        <Box
          style={{
            position: "relative",
            width: timelineWidth,
            maxWidth: useScrollableTimeline ? "none" : "100%",
            height: alternateTimelineLabels ? 176 : 126,
            margin: "0 auto",
          }}
        >
          <Box
            style={{
              position: "absolute",
              left: sortedCards.length === 1 ? "50%" : `${timelineInset}%`,
              right: sortedCards.length === 1 ? "50%" : `${timelineInset}%`,
              top: 80,
              height: 3,
              borderRadius: 4,
              background: `linear-gradient(90deg, ${primaryColor}55, ${primaryColor}cc)`,
              pointerEvents: "none",
            }}
          />
          {sortedCards.map((card, index) => {
            const active = index === selectedIndex;
            const labelBelow = alternateTimelineLabels && index % 2 === 1;
            return (
              <button
                type="button"
                key={`${card.id || index}-${card.dateLabel || index}`}
                data-timeline-index={index}
                onClick={() => selectCard(index)}
                title={card.displayTitle}
                style={{
                  position: "absolute",
                  left: `${getPosition(card, index)}%`,
                  top: labelBelow ? 104 : 4,
                  transform: "translateX(-50%)",
                  width: 164,
                  minHeight: 56,
                  padding: "9px 12px",
                  borderRadius: 10,
                  border: active
                    ? `2px solid ${primaryColor}`
                    : "1px solid #ddd",
                  background: active ? "#fff" : "#f4f4f4",
                  color: active ? primaryColor : "#666",
                  fontSize: 12,
                  fontWeight: active ? 800 : 600,
                  cursor: "pointer",
                  zIndex: 1,
                  boxShadow: active
                    ? "0 7px 20px rgba(0,0,0,0.13)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
                  transition:
                    "border-color 180ms ease, color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
                }}
              >
                {card.displayTitle && (
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {normalizeGermanQuotes(card.displayTitle)}
                  </span>
                )}
                {card.dateLabel && (
                  <span
                    style={{
                      display: "block",
                      marginTop: 3,
                      color: active ? primaryColor : "#999",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {moment(card.dateLabel).format("DD.MM.YYYY")}
                  </span>
                )}
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: labelBelow ? -29 : 68,
                    transform: "translateX(-50%)",
                    width: active ? 16 : 12,
                    height: active ? 16 : 12,
                    borderRadius: "50%",
                    backgroundColor: active ? primaryColor : "#aaa",
                    border: "3px solid #fff",
                    boxShadow: active ? `0 0 0 4px ${primaryColor}22` : "none",
                    transition: "all 180ms ease",
                  }}
                />
              </button>
            );
          })}
        </Box>
      </div>
    </Box>
  );
}
